// src/.MODULS/UCAT_EXAM_MODUL/Pages/ReviewScreen/UcatTestReview.tsx
import React, { useState, useReducer, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    examReducer,
    initialState,
} from "../../Components/PracticeTestReducer/TestReducer";
import {
    SplitPane,
    SinglePageQuestion,
    NavigatorModal,
    ExplanationModal,
} from "../../Components/Practice_Test/Exam_component";
import { UcatLayout } from "../../Components/Shared";
import {
    ChevronLeft,
    ChevronRight,
    LogOut,
    CheckCircle,
    XCircle,
    AlertCircle,
    Flag,
} from "lucide-react";
import { listReViewExamID } from "../../../../Redux/Slices/ReViewExam/ListReViewExamSlice";
import { listReViewExamIDPractice } from "../../../../Redux/Slices/ReViewExamPractice/ListReViewExamPracticeSlice";
import { CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { UserAnswer, AnswerType } from "../../Components/sample data/Type";
import { addFalgExam } from "../../../../Redux/Slices/FalgExam/AddFalgExamSlice";

export const UcatTestReview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Get studentExamId and filterType from location state
    const navigationState = location.state as any;
    const studentExamId = navigationState?.studentExamId || navigationState;
    const filterType = navigationState?.filterType || "all"; // "all", "incorrect", "flagged"

    console.log("🔍 Navigation state:", { studentExamId, filterType });

    const { data: listReViewExamIDSlice, isLoading: LoadingMock } = useAppSelector(
        (state) => state.ListReViewExamIDSlice || {}
    );

    const { data: listReViewExamPracticeSlice, isLoading: LoadingPractice } = useAppSelector(
        (state: any) => state.ListReViewExamIDPracticeSlice || {}
    );

    const isLoading = LoadingMock || LoadingPractice;

    const [state, dispatchReducer] = useReducer(examReducer, initialState);
    const [showNavigator, setShowNavigator] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);

    // Map server response data to frontend UserAnswer format
    const mapServerAnswersToFrontendFormat = (questions: any[]): Record<number, UserAnswer> => {
        const mappedAnswers: Record<number, UserAnswer> = {};

        questions.forEach((q: any) => {
            const qId = q.question_id || q.id;
            const chosenOption = q.options?.find((opt: any) => opt.chosen);
            const selectedOptionIndex = chosenOption ? q.options.findIndex(
                (opt: any) => opt.option_id === chosenOption.option_id
            ) : null;

            let dragDropAnswers = undefined;
            if (q.answer_type === AnswerType.DRAG_DROP) {
                const rawAnswers = q.answer_data || q.sentence;
                // Prioritize answer_items as it contains the processed structured review data
                if (q.answer_items && Array.isArray(q.answer_items) && q.answer_items.length > 0) {
                    dragDropAnswers = {};
                    q.answer_items.forEach((item: any) => {
                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                        if (subId) {
                            // "value" is what the user dragged into the bucket
                            dragDropAnswers[subId] = item.value || item.answer || null;
                        }
                    });
                } else if (rawAnswers && typeof rawAnswers === 'string' && rawAnswers !== "null" && rawAnswers !== "{}" && rawAnswers.trim() !== "") {
                    try {
                        dragDropAnswers = JSON.parse(rawAnswers);
                    } catch (e) {
                        console.error("Failed to parse review drag drop answer", e);
                    }
                }
            }

            if (qId) {
                mappedAnswers[Number(qId)] = {
                    selectedOptionIndex,
                    dragDropAnswers,
                    isFlagged: q.flagged === true || q.isFlagged === true || q.is_flagged === true || q.is_flagged === 1 || q.is_flagged === "true",
                    isViewed: true,
                };
            }
        });

        return mappedAnswers;
    };

    const reviewDataRaw = listReViewExamIDSlice?.data || listReViewExamPracticeSlice?.data || listReViewExamIDSlice || listReViewExamPracticeSlice;
    const reviewData: any = reviewDataRaw;
    const allQuestions = reviewData?.questions || [];

    // Memoize normalized questions so they don't change on every render
    const normalizedQuestions = React.useMemo(() => {
        return allQuestions.map((q: any) => {
            let finalOptions = (q.options || []).map((opt: any) => {
                const isChosenByText = q.answer !== undefined && q.answer !== null &&
                    String(opt.option_text || opt.option || "").toLowerCase() === String(q.answer).toLowerCase();
                const isChosenByBool = q.answer !== undefined && q.answer !== null && (
                    (String(q.answer).toLowerCase() === "true" && (String(opt.option_text).toLowerCase() === "yes" || String(opt.option_text).toLowerCase() === "true")) ||
                    (String(q.answer).toLowerCase() === "false" && (String(opt.option_text).toLowerCase() === "no" || String(opt.option_text).toLowerCase() === "false"))
                );

                return {
                    ...opt,
                    option_id: opt.option_id || opt.id || null,
                    option_text: opt.option_text || opt.option || opt.answer || null,
                    option_url: opt.option_url || opt.image_url || null,
                    chosen: opt.chosen || q.chosen_option_id === opt.option_id || q.chosen_option_id === opt.id || isChosenByText || isChosenByBool,
                };
            });

            if (q.answer_type === AnswerType.TRUE_FALSE && (!finalOptions || finalOptions.length === 0)) {
                const qAnswerLower = String(q.answer || "").toLowerCase();
                finalOptions = [
                    {
                        option_id: -1,
                        option_text: "Yes",
                        option_url: null,
                        chosen: q.chosen_option_id === -1 || qAnswerLower === "true" || qAnswerLower === "yes"
                    },
                    {
                        option_id: -2,
                        option_text: "No",
                        option_url: null,
                        chosen: q.chosen_option_id === -2 || qAnswerLower === "false" || qAnswerLower === "no"
                    },
                ];
            }

            return {
                question_id: q.question_id || q.id,
                question_type_id: q.question_type_id || 0,
                question_type_name: q.question_type_name || "Review",
                question_text: q.question_text || q.question || "",
                question_url: q.question_url || q.image_url || null,
                prompt: q.prompt || null,
                prompt_url: q.prompt_url || null,
                instruction: q.instruction || null,
                imgWidth: q.imgWidth || 800,
                prompt_imgWidth: q.prompt_imgWidth || 800,
                source_type: q.source_type || "internal",
                subtype_id: q.subtype_id || null,
                subtype_name: q.subtype_name || null,
                options: finalOptions,
                answer_type: q.answer_type || AnswerType.MCQ,
                answer_items: q.answer_items || null,
                answer_data: q.answer_data || null,
                sentence: q.sentence || null,
                drag_drop_data: (q.answer_type === AnswerType.DRAG_DROP && q.answer_items && Array.isArray(q.answer_items))
                    ? q.answer_items.map((item: any) => ({ ...item.drag_drop }))
                    : (q.drag_drop_data || null),
                drag_drop_pattern: q.drag_drop_pattern || null,
                correctAnswerIndex: finalOptions ? finalOptions.findIndex((opt: any) => opt.is_correct) : -1,
                explanation: q.explanation || q.answer_explanation || q.answer_explanation_text,
                isFlagged: q.flagged === true || q.isFlagged === true || q.is_flagged === true || q.is_flagged === 1 || q.is_flagged === "true",
            };
        });
    }, [allQuestions]);

    // Memoize filtered questions
    const filteredQuestions = React.useMemo(() => {
        let qs = normalizedQuestions;
        if (filterType === "incorrect") {
            qs = normalizedQuestions.filter((q: any) => {
                const chosenOption = q.options.find((opt: any) => opt.chosen);
                return chosenOption && !chosenOption.is_correct;
            });
        } else if (filterType === "flagged") {
            qs = normalizedQuestions.filter((q: any) => {
                return q.isFlagged === true;
            });
        }
        return qs;
    }, [normalizedQuestions, filterType]);

    // Track initialization state to prevent resetting filters/index on every render
    const isInitialized = React.useRef(false);

    // Initialize state with mapped answers when filtered questions are available
    useEffect(() => {
        if (filteredQuestions.length > 0 && !isInitialized.current) {
            const mappedAnswers = mapServerAnswersToFrontendFormat(filteredQuestions);
            dispatchReducer({
                type: "LOAD_EXISTING_ANSWERS",
                answers: mappedAnswers
            });

            // If we came from a specific question click, jump to it
            const initialIdx = navigationState?.initialQuestionIndex;
            if (typeof initialIdx === 'number' && initialIdx >= 0 && initialIdx < filteredQuestions.length) {
                dispatchReducer({
                    type: "START_SECTION",
                    time: 0,
                    index: initialIdx
                });
            }

            console.log("🔄 Initialized review session with answers.");
            isInitialized.current = true;
        }
    }, [filteredQuestions, navigationState?.initialQuestionIndex]);

    useEffect(() => {
        if (studentExamId) {
            console.log("🔍 Loading review data (Mock & Practice) for exam ID:", studentExamId);
            dispatch(listReViewExamID({ id: studentExamId }));
            // dispatch(listReViewExamIDPractice({ id: studentExamId }));
        }
    }, [dispatch, studentExamId]);

    // Security measures to block inspection tools
    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
        };

        const handleKeyDownSecurity = (e: KeyboardEvent) => {
            // Block F12
            if (e.key === "F12") {
                e.preventDefault();
                return;
            }

            // Block Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C (Cmd+Option+I/J/C on Mac)
            if (
                (e.ctrlKey || e.metaKey) &&
                e.shiftKey &&
                (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")
            ) {
                e.preventDefault();
                return;
            }

            // Block Ctrl+U (View Source)
            if ((e.ctrlKey || e.metaKey) && (e.key === "U" || e.key === "u")) {
                e.preventDefault();
                return;
            }
        };

        window.addEventListener("contextmenu", handleContextMenu);
        window.addEventListener("keydown", handleKeyDownSecurity, true);

        return () => {
            window.removeEventListener("contextmenu", handleContextMenu);
            window.removeEventListener("keydown", handleKeyDownSecurity, true);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <CircularProgress />
            </div>
        );
    }

    if (allQuestions.length === 0) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600 mb-4">No review data available.</p>
                <button onClick={() => navigate("/welcome_ucat")} className="px-4 py-2 bg-blue-600 text-white rounded">Return to Home</button>
            </div>
        );
    }

    if (filteredQuestions.length === 0) {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-white">
                <p className="text-gray-600 mb-4">
                    {filterType === "incorrect"
                        ? "No incorrect questions found. Great job!"
                        : filterType === "flagged"
                            ? "No flagged questions found."
                            : "No questions available."}
                </p>
                <button onClick={() => navigate("/welcome_ucat")} className="px-4 py-2 bg-blue-600 text-white rounded">Return to Home</button>
            </div>
        );
    }

    const section = {
        id: 0,
        title: reviewData?.exam_name || "UCAT Exam Review",
        questions: filteredQuestions,
    };

    const question = section.questions[state.currentQuestionIndex];
    if (!question) {
        return (
            <div className="flex h-screen items-center justify-center bg-white">
                <CircularProgress />
            </div>
        );
    }

    // Determine question status
    const userAnswer = state.answers[question.question_id];
    const isDragDrop = question.answer_type === AnswerType.DRAG_DROP;

    // Determine question status
    let isAnswered = false;
    let isCorrect = false;

    if (isDragDrop) {
        // Fallback: Check if we have answers in the state OR in the raw question data
        const ddAnswers = userAnswer?.dragDropAnswers || {};

        // If state answers are empty, try to derive from raw answer_items 
        // (This handles cases where the state might not have initialized yet)
        const effectiveDdAnswers = (Object.keys(ddAnswers).length > 0)
            ? ddAnswers
            : (() => {
                const derived: any = {};
                if (question.answer_items && Array.isArray(question.answer_items)) {
                    question.answer_items.forEach((item: any) => {
                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                        if (subId) derived[subId] = item.value || item.answer;
                    });
                }
                return derived;
            })();

        const items = question.drag_drop_data || [];
        const assignedCount = Object.values(effectiveDdAnswers).filter(v => v !== null && v !== "").length;
        isAnswered = assignedCount > 0;

        if (items.length > 0 && assignedCount === items.length) {
            // Check if all items are correct (case-insensitive)
            isCorrect = items.every((item: any) => {
                const userVal = String(effectiveDdAnswers[item.id] || "").toLowerCase().trim();
                const targetVal = String(item.target_bucket || "").toLowerCase().trim();
                return userVal === targetVal && userVal !== "";
            });
        }
    } else {
        const chosenOption = question.options.find((opt: any) => opt.chosen);
        isAnswered = !!chosenOption;
        isCorrect = chosenOption?.is_correct || false;
    }

    let statusText = "Unseen";
    let statusColor = "text-gray-600";
    let statusBg = "bg-gray-100";
    let StatusIcon = AlertCircle;

    if (isAnswered) {
        if (isCorrect) {
            statusText = "Correct";
            statusColor = "text-green-600";
            statusBg = "bg-green-100";
            StatusIcon = CheckCircle;
        } else {
            statusText = "Incorrect";
            statusColor = "text-red-600";
            statusBg = "bg-red-100";
            StatusIcon = XCircle;
        }
    }

    // Determine layout (split-pane or single-page)
    const hasPassage =
        !!question.prompt || !!question.prompt_url || !!question.instruction;
    const shouldUseSplitLayout =
        (question.question_type_name === "Reading Comprehension" ||
            question.question_type_name === "Verbal Reasoning" ||
            (question.question_type_name === "Quantitative Reasoning" &&
                hasPassage)) &&
        hasPassage;

    // Set user answer to show selected option (read-only)
    const currentChosenOption = !isDragDrop ? question.options.find((opt: any) => opt.chosen) : null;
    const selectedOptionIndex = currentChosenOption
        ? question.options.findIndex(
            (opt: any) => opt.option_id === currentChosenOption.option_id
        )
        : null;

    const onPrevious = () => {
        if (state.currentQuestionIndex > 0) {
            const currentQuestion = section.questions[state.currentQuestionIndex];
            dispatchReducer({
                type: "VIEW_QUESTION",
                questionId: currentQuestion.question_id
            });
            dispatchReducer({ type: "PREV_QUESTION" });
        }
    };

    const onNext = () => {
        if (state.currentQuestionIndex < section.questions.length - 1) {
            const currentQuestion = section.questions[state.currentQuestionIndex];
            dispatchReducer({
                type: "VIEW_QUESTION",
                questionId: currentQuestion.question_id
            });
            dispatchReducer({ type: "NEXT_QUESTION" });
        }
    };

    const handleEndReview = () => {
        navigate("/ucat_examresult_practice");
    };

    const handleToggleFlag = () => {
        if (studentExamId && question.question_id) {
            dispatch(
                addFalgExam({
                    student_exam_id: studentExamId,
                    question_id: question.question_id,
                })
            );
        }

        dispatchReducer({
            type: "TOGGLE_FLAG",
            questionId: question.question_id,
        });
    };

    return (
        <UcatLayout
            headerTitle={section.title}
            headerSubtitle={`Question ${state.currentQuestionIndex + 1} of ${section.questions.length}`}
            showToolbar={true}
            showCalculatorButton={true}
            showExplainAnswerButton={true}
            onExplainAnswerClick={() => setShowExplanation(true)}
            toolbarRightActions={
                <div className="flex items-center h-full gap-4">
                    <div
                        className={`flex items-center gap-1.5 px-3 py-1 ${statusBg} ${statusColor} rounded-md font-medium text-[13px]`}
                    >
                        <StatusIcon size={16} />
                        <span>{statusText}</span>
                    </div>

                    <button
                        onClick={handleToggleFlag}
                        className="flex items-center gap-1 text-white hover:text-[#FFFF00] px-2 h-full transition-colors"
                    >
                        <Flag
                            size={18}
                            className={
                                userAnswer?.isFlagged ? "fill-orange-400 text-orange-400" : "text-white/90"
                            }
                        />
                        <span className="text-[16px] font-normal">
                            <span className="underline decoration-1 underline-offset-2" style={{ fontSize: "17px" }}>F</span>{userAnswer?.isFlagged ? "lagged" : "lag"} for Review
                        </span>
                    </button>
                </div>
            }
            footerProps={{
                onEndExam: handleEndReview,
                onNext: state.currentQuestionIndex < section.questions.length - 1 ? onNext : undefined,
                onPrevious: state.currentQuestionIndex > 0 ? onPrevious : undefined,
                onNavigator: () => setShowNavigator(true),
                showNavigator: true,
                showPrevious: state.currentQuestionIndex > 0,
                nextDisabled: state.currentQuestionIndex === section.questions.length - 1,
                previousDisabled: state.currentQuestionIndex === 0,
                leftButtonText: "End Review",
                rightButtonText: "Next"
            }}
        >
            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                <div className="p-1 flex items-center gap-2 text-[#000]-700 w-full">
                    <div className="flex-1 text-center text-[19px] font-[400] pr-10 mb-[10px] f-tahoma">
                        {reviewData?.exam_name?.split(' ')[0]} Review: use this screen to review your responses and explanations
                    </div>
                </div>
                <div className="flex-1 overflow-hidden">
                    {shouldUseSplitLayout ? (
                        <SplitPane
                            passage={question.prompt || question.instruction || undefined}
                            promptUrl={question.prompt_url || undefined}
                            question={question}
                            selectedOptionIndex={selectedOptionIndex}
                            dragDropAnswers={(() => {
                                const dd = userAnswer?.dragDropAnswers || {};
                                if (Object.keys(dd).length > 0) return dd;
                                // Fallback for dragDropAnswers if state is not ready
                                const derived: any = {};
                                if (question.answer_items && Array.isArray(question.answer_items)) {
                                    question.answer_items.forEach((item: any) => {
                                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                                        if (subId) derived[subId] = item.value || item.answer;
                                    });
                                }
                                return derived;
                            })()}
                            onOptionSelect={() => { }} // Read-only, no action
                            isReviewMode={true}
                            onScrollStatusChange={() => { }}
                        />
                    ) : (
                        <SinglePageQuestion
                            question={question}
                            selectedOptionIndex={selectedOptionIndex}
                            dragDropAnswers={(() => {
                                const dd = userAnswer?.dragDropAnswers || {};
                                if (Object.keys(dd).length > 0) return dd;
                                // Fallback for dragDropAnswers if state is not ready
                                const derived: any = {};
                                if (question.answer_items && Array.isArray(question.answer_items)) {
                                    question.answer_items.forEach((item: any) => {
                                        const subId = item.drag_drop?.id || item.drag_drop_id || item.id;
                                        if (subId) derived[subId] = item.value || item.answer;
                                    });
                                }
                                return derived;
                            })()}
                            onOptionSelect={() => { }} // Read-only, no action
                            isReviewMode={true}
                        />
                    )}
                </div>

                {/* Navigator Modal */}
                {showNavigator && (
                    <NavigatorModal
                        sections={[section]}
                        currentSectionIndex={0}
                        currentQuestionIndex={state.currentQuestionIndex}
                        onClose={() => setShowNavigator(false)}
                        onJump={(sectionIdx: number, questionIdx: number) => {
                            // Track view before jumping
                            const currentQuestion = section.questions[state.currentQuestionIndex];
                            dispatchReducer({
                                type: "VIEW_QUESTION",
                                questionId: currentQuestion.question_id
                            });
                            // Then jump to the target question
                            dispatchReducer({
                                type: "JUMP_QUESTION",
                                index: questionIdx,
                            });
                            setShowNavigator(false);
                        }}
                        answers={state.answers}
                        isOpen={showNavigator}
                    />
                )}

                <ExplanationModal
                    isOpen={showExplanation}
                    onClose={() => setShowExplanation(false)}
                    correctAnswerIndex={question.correctAnswerIndex}
                    explanation={question.explanation}
                />
            </div>
        </UcatLayout>
    );
};

export default UcatTestReview;
